import React, { useEffect, useRef, useState } from 'react'

import * as S from './styles'

/* eslint-disable arrow-body-style */
export interface InputProps {
  /**
   * Label of the input
   *
   * @example
   * ```tsx
   * <Input label="Email" />
   * ```
   */
  label: string
  /**
   * Name of the input
   *
   * @example
   * ```tsx
   * <Input name="email" />
   * ```
   */
  name: string
  /**
   * Id of the input
   *
   * @example
   * ```tsx
   * <Input id="email" />
   * ```
   */
  id?: string
  /**
   * Placeholder of the input
   *
   * @example
   * ```tsx
   * <Input placeholder="Email" />
   * ```
   */
  placeholder: string
  /**
   * Type of the input
   *
   * @example
   * ```tsx
   * <Input type="email" />
   * ```
   */
  type: string
  /**
   * Value of the input
   *
   * @example
   * ```tsx
   * <Input value="email" />
   * ```
   */
  value: string
  /**
   * Function that is called when the input is changed
   *
   * @example
   * ```tsx
   * <Input onChange={handleChange} />
   * ```
   */
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  /**
   * Error message of the input
   *
   * @example
   * ```tsx
   * <Input error="Email is required" />
   * ```
   */
  error?: string | null
  /**
   * If the input is disabled
   *
   * @example
   * ```tsx
   * <Input disabled />
   * ```
   */
  disabled?: boolean
  /**
   * Width of the input
   * @example
   * ```tsx
   * <Input width="100%" />
   * ```
   * */
  width?: string
  /**
   * Enabled dark mode
   * @example
   * ```tsx
   * <Input darkMode=true />
   * ```
   * */
  darkMode?: boolean
  /**
   * Function that is called when the input is blur
   * @example
   * ```tsx
   * <Input onBlur={handleBlur}/>
   * ```
   * */
  onBlur?: () => void
  /**
   * Max length of the input value
   *
   * @example
   * ```tsx
   * <Input maxLength={10} />
   * ```
   */
  maxLength?: number
  className?: string
  autoFocus?: boolean
  onFocus?: (e: React.FocusEvent<HTMLInputElement, Element>) => void
}

export const Input: React.FC<InputProps> = ({
  label,
  name,
  placeholder,
  type,
  value,
  onChange,
  error,
  disabled,
  width,
  darkMode,
  id,
  onBlur,
  className,
  autoFocus = false,
  onFocus,
  ...props
}) => {
  const [hiddenError, setHiddenError] = useState(false)

  const input = useRef<HTMLInputElement>(null)
  const handleFocusIn = () => setHiddenError(true)
  const handleFocusOut = () => setHiddenError(false)

  useEffect(() => {
    const newInput = input.current
    if (newInput) {
      newInput.addEventListener('focusin', handleFocusIn)
      newInput.addEventListener('focusout', handleFocusOut)
    }
    if (autoFocus) {
      newInput?.select()
    }
    return () => {
      if (newInput) {
        newInput.removeEventListener('focusin', handleFocusIn)
        newInput.removeEventListener('focusout', handleFocusOut)
      }
    }
  }, [autoFocus])

  return (
    <S.Wrapper width={width} className={className}>
      <S.Label htmlFor={id ?? name} darkMode={darkMode}>
        {label}
      </S.Label>
      <S.Input
        ref={input}
        className="input"
        type={type}
        name={name}
        id={id ?? name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        darkMode={darkMode}
        error={Boolean(error)}
        onBlur={onBlur}
        onFocus={onFocus}
        {...props}
      />
      {error && !hiddenError && <S.Error>{error}</S.Error>}
    </S.Wrapper>
  )
}
